import React, { useMemo } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { defaultTooltipConfig, defaultGridConfig, defaultLegendConfig, defaultScaleTitleConfig } from '../../utils/chartConfig';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend
);

const PRIMARY = '#072040';

const WithdrawalRateChart = React.forwardRef(({ portfolioIncome, balance, years, age1, initialBalance }, ref) => {
  const labels = years.map((y) => `${y}`);

  const withdrawalRates = useMemo(() => {
    return years.map((_, i) => {
      const inc = portfolioIncome[i] || 0;
      // Use start-of-period balance: initial balance for year 0, previous year's ending balance thereafter
      const startBalance = i === 0 ? (initialBalance || balance[0]) : balance[i - 1];
      if (!startBalance || startBalance <= 0) return 0;
      const rate = (inc / startBalance) * 100;
      return Math.min(rate, 100);
    });
  }, [portfolioIncome, balance, years, initialBalance]);

  const data = {
    labels,
    datasets: [
      {
        label: 'Withdrawal Rate',
        data: withdrawalRates,
        borderColor: PRIMARY,
        backgroundColor: 'rgba(0, 51, 102, 0.1)',
        borderWidth: 2,
        pointRadius: 2,
        pointHoverRadius: 5,
        tension: 0.3,
        fill: true,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index',
      intersect: false,
    },
    plugins: {
      title: {
        display: true,
        text: 'Withdrawal Rate Over Time (50th Percentile)',
        font: { size: 14 },
      },
      legend: defaultLegendConfig,
      tooltip: {
        ...defaultTooltipConfig,
        callbacks: {
          title: (items) => {
            const idx = items[0]?.dataIndex;
            const year = years[idx];
            const age = age1 != null ? age1 + year : null;
            return age != null ? `Year ${year} (Age ${age})` : `Year ${year}`;
          },
          label: (item) => {
            return `${item.dataset.label}: ${item.raw.toFixed(1)}%`;
          },
        },
      },
    },
    scales: {
      x: {
        grid: defaultGridConfig,
        title: {
          ...defaultScaleTitleConfig,
          text: 'Age',
        },
        ticks: {
          callback: function (value, index) {
            const year = years[index];
            if (age1 != null) {
              return `${age1 + year}`;
            }
            return `${year}`;
          },
          maxRotation: 0,
          autoSkip: true,
          maxTicksLimit: 15,
        },
      },
      y: {
        grid: defaultGridConfig,
        title: {
          ...defaultScaleTitleConfig,
          text: 'Withdrawal Rate (%)',
        },
        min: 0,
        max: undefined,
        ticks: {
          callback: (value) => `${value}%`,
        },
      },
    },
  };

  return (
    <div style={{ height: '350px', position: 'relative' }}>
      <Line ref={ref} data={data} options={options} />
    </div>
  );
});

WithdrawalRateChart.displayName = 'WithdrawalRateChart';

export default WithdrawalRateChart;
